import React, { useState, useEffect, useRef, useContext } from 'react';
import AppContext from 'context/Context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faClock,
    faSave,
    faSpinner,
    faEdit,
    faTimes
} from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

const DIAS_SEMANA_DEFAULT = [
    { iDiaSemana: 1, vDiaSemana: 'Lunes', dHoraInicio: '09:00:00', dHoraFin: '18:00:00', bLaboral: true },
    { iDiaSemana: 2, vDiaSemana: 'Martes', dHoraInicio: '09:00:00', dHoraFin: '18:00:00', bLaboral: true },
    { iDiaSemana: 3, vDiaSemana: 'Miércoles', dHoraInicio: '09:00:00', dHoraFin: '18:00:00', bLaboral: true },
    { iDiaSemana: 4, vDiaSemana: 'Jueves', dHoraInicio: '09:00:00', dHoraFin: '18:00:00', bLaboral: true },
    { iDiaSemana: 5, vDiaSemana: 'Viernes', dHoraInicio: '09:00:00', dHoraFin: '18:00:00', bLaboral: true },
    { iDiaSemana: 6, vDiaSemana: 'Sábado', dHoraInicio: '09:00:00', dHoraFin: '14:00:00', bLaboral: true },
    { iDiaSemana: 7, vDiaSemana: 'Domingo', dHoraInicio: null, dHoraFin: null, bLaboral: false }
];

const BusinessHoursSettingsSection = () => {
    const { config: { isDark } = {} } = useContext(AppContext);
    const { token, nombreCompleto } = useToken();
    const { showSuccessToast, showErrorToast } = useToast();

    const [horarios, setHorarios] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const backupHorariosRef = useRef([]);

    // Cargar catálogo de horarios desde el backend
    const fetchHorarios = async () => {
        setIsLoading(true);
        try {
            const response = await fetch('/api/SupportEngine/GetHorarios', {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': 'Bearer ' + token
                }
            });

            if (response.ok) {
                const result = await response.json();
                const list = result.data || result;
                if (Array.isArray(list) && list.length > 0) {
                    setHorarios(list);
                } else {
                    setHorarios(DIAS_SEMANA_DEFAULT);
                }
            } else {
                setHorarios(DIAS_SEMANA_DEFAULT);
            }
        } catch (err) {
            console.error('Error al cargar horarios:', err);
            setHorarios(DIAS_SEMANA_DEFAULT);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (token) {
            fetchHorarios();
        }
    }, [token]);

    const handleChange = (index, field, value) => {
        setHorarios(prev => {
            const copy = [...prev];
            copy[index] = { ...copy[index], [field]: value };
            return copy;
        });
    };

    // Activar o desactivar el día (Laboral / Inactivo)
    const handleToggleLaboral = (index, checked) => {
        setHorarios(prev => {
            const copy = [...prev];
            const current = copy[index];
            copy[index] = {
                ...current,
                bLaboral: checked,
                dHoraInicio: checked ? (current.dHoraInicio || '09:00:00') : current.dHoraInicio,
                dHoraFin: checked ? (current.dHoraFin || (current.iDiaSemana === 6 ? '14:00:00' : '18:00:00')) : current.dHoraFin
            };
            return copy;
        });
    };

    // Iniciar edición
    const handleStartEdit = () => {
        backupHorariosRef.current = JSON.parse(JSON.stringify(horarios));
        setIsEditing(true);
    };

    // Cancelar edición y restaurar valores
    const handleCancelEdit = () => {
        if (backupHorariosRef.current.length > 0) {
            setHorarios(backupHorariosRef.current);
        }
        setIsEditing(false);
    };

    // Guardar edición de todos los horarios
    const handleSaveAll = async () => {
        setIsSaving(true);
        try {
            const requests = horarios.map(item => {
                const payload = {
                    idHorarioServicio: item.idHorarioServicio || null,
                    iDiaSemana: item.iDiaSemana,
                    vDiaSemana: item.vDiaSemana,
                    dHoraInicio: item.bLaboral ? (item.dHoraInicio ? item.dHoraInicio.substring(0, 8) : '09:00:00') : null,
                    dHoraFin: item.bLaboral ? (item.dHoraFin ? item.dHoraFin.substring(0, 8) : '18:00:00') : null,
                    bLaboral: !!item.bLaboral,
                    vUser: nombreCompleto || 'Agente'
                };

                return fetch('/api/SupportEngine/SaveHorario', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Authorization': 'Bearer ' + token
                    },
                    body: JSON.stringify(payload)
                });
            });

            const responses = await Promise.all(requests);
            const allOk = responses.every(res => res.ok);

            if (allOk) {
                if (showSuccessToast) {
                    showSuccessToast('Horarios de atención actualizados con éxito.');
                }
                setIsEditing(false);
                await fetchHorarios();
            } else {
                if (showErrorToast) {
                    showErrorToast('Ocurrió un error al guardar uno o más horarios.');
                }
            }
        } catch (err) {
            if (showErrorToast) {
                showErrorToast('Error de conexión al guardar los horarios.');
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="se-card-panel mb-4">
            {/* Header Sección */}
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div>
                    <h5 className="fw-bold mb-1 d-flex align-items-center gap-2" style={{ fontSize: '1.15rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
                        <div
                            className="d-flex align-items-center justify-content-center rounded-circle"
                            style={{
                                width: '32px',
                                height: '32px',
                                backgroundColor: isDark ? 'rgba(37, 99, 235, 0.2)' : '#dbeafe',
                                color: isDark ? '#60a5fa' : '#2563eb'
                            }}
                        >
                            <FontAwesomeIcon icon={faClock} style={{ fontSize: '0.9rem' }} />
                        </div>
                        <span>Horarios de Atención y Servicio</span>
                    </h5>
                    <p className="small mb-0" style={{ fontSize: '0.84rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                        Configura los días laborales y rangos de atención del equipo de soporte para la respuesta automática y guardias.
                    </p>
                </div>

                {/* Botón Ternario de Edición */}
                <div className="d-flex align-items-center gap-2">
                    {isEditing && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2"
                            onClick={handleCancelEdit}
                            disabled={isSaving}
                            style={{ fontSize: '0.84rem', borderRadius: '6px' }}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                            <span>Cancelar</span>
                        </button>
                    )}

                    <button
                        type="button"
                        className={`btn btn-sm d-flex align-items-center gap-2 ${isEditing ? 'btn-success' : 'btn-primary'}`}
                        onClick={isEditing ? handleSaveAll : handleStartEdit}
                        disabled={isLoading || isSaving}
                        style={{ fontSize: '0.84rem', borderRadius: '6px' }}
                    >
                        {isSaving ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>Guardando...</span>
                            </>
                        ) : isEditing ? (
                            <>
                                <FontAwesomeIcon icon={faSave} />
                                <span>Guardar Edición</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faEdit} />
                                <span>Editar Horario</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Tabla de Horarios */}
            {isLoading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem' }} />
                    <p className="small mt-2 mb-0" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>Cargando configuración de horarios...</p>
                </div>
            ) : (
                <div
                    className="table-responsive rounded-3 border shadow-xs"
                    style={{ backgroundColor: isDark ? '#121e2d' : '#ffffff', borderColor: isDark ? '#233142' : '#e2e8f0' }}
                >
                    <table className="table align-middle mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0, backgroundColor: isDark ? '#121e2d' : '#ffffff' }}>
                        <thead>
                            <tr style={{ borderBottom: isDark ? '1px solid #233142' : '1px solid #e2e8f0' }}>
                                <th className="py-3 px-3 fw-bold text-uppercase" style={{ fontSize: '0.74rem', letterSpacing: '0.04em', color: isDark ? '#94a3b8' : '#64748b', width: '22%' }}>
                                    DÍA DE LA SEMANA
                                </th>
                                <th className="py-3 px-3 fw-bold text-uppercase" style={{ fontSize: '0.74rem', letterSpacing: '0.04em', color: isDark ? '#94a3b8' : '#64748b', width: '25%' }}>
                                    HORA INICIO
                                </th>
                                <th className="py-3 px-3 fw-bold text-uppercase" style={{ fontSize: '0.74rem', letterSpacing: '0.04em', color: isDark ? '#94a3b8' : '#64748b', width: '25%' }}>
                                    HORA FIN
                                </th>
                                <th className="py-3 px-3 fw-bold text-uppercase" style={{ fontSize: '0.74rem', letterSpacing: '0.04em', color: isDark ? '#94a3b8' : '#64748b', width: '18%' }}>
                                    ESTADO
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {horarios.map((item, idx) => {
                                const isLaboral = item.bLaboral;
                                const horaInicioValue = item.dHoraInicio ? item.dHoraInicio.substring(0, 5) : '';
                                const horaFinValue = item.dHoraFin ? item.dHoraFin.substring(0, 5) : '';

                                return (
                                    <tr
                                        key={item.iDiaSemana || idx}
                                        style={{ borderBottom: isDark ? '1px solid #233142' : '1px solid #f1f5f9' }}
                                    >
                                        {/* DÍA */}
                                        <td className="py-3 px-3 align-middle">
                                            <div className="fw-bold" style={{ fontSize: '0.92rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
                                                {item.vDiaSemana}
                                            </div>
                                        </td>

                                        {/* HORA INICIO */}
                                        <td className="py-3 px-3 align-middle">
                                            <input
                                                type="time"
                                                className="form-control form-control-sm"
                                                value={horaInicioValue}
                                                disabled={!isEditing || !isLaboral}
                                                onChange={e => handleChange(idx, 'dHoraInicio', e.target.value ? `${e.target.value}:00` : null)}
                                                style={{
                                                    maxWidth: '140px',
                                                    backgroundColor: isDark
                                                        ? (isEditing && isLaboral ? '#182635' : '#111827')
                                                        : (isEditing && isLaboral ? '#ffffff' : '#f1f5f9'),
                                                    borderColor: isDark ? '#233142' : '#ced4da',
                                                    color: isDark ? '#f9fafd' : '#0f172a',
                                                    opacity: isEditing && isLaboral ? 1 : 0.65
                                                }}
                                            />
                                        </td>

                                        {/* HORA FIN */}
                                        <td className="py-3 px-3 align-middle">
                                            <input
                                                type="time"
                                                className="form-control form-control-sm"
                                                value={horaFinValue}
                                                disabled={!isEditing || !isLaboral}
                                                onChange={e => handleChange(idx, 'dHoraFin', e.target.value ? `${e.target.value}:00` : null)}
                                                style={{
                                                    maxWidth: '140px',
                                                    backgroundColor: isDark
                                                        ? (isEditing && isLaboral ? '#182635' : '#111827')
                                                        : (isEditing && isLaboral ? '#ffffff' : '#f1f5f9'),
                                                    borderColor: isDark ? '#233142' : '#ced4da',
                                                    color: isDark ? '#f9fafd' : '#0f172a',
                                                    opacity: isEditing && isLaboral ? 1 : 0.65
                                                }}
                                            />
                                        </td>

                                        {/* SWITCH ESTADO */}
                                        <td className="py-3 px-3 align-middle">
                                            <div className="form-check form-switch m-0 d-flex align-items-center gap-2">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    role="switch"
                                                    id={`switchDia_${item.iDiaSemana}`}
                                                    checked={isLaboral}
                                                    disabled={!isEditing}
                                                    onChange={e => handleToggleLaboral(idx, e.target.checked)}
                                                    style={{ cursor: isEditing ? 'pointer' : 'default', opacity: isEditing ? 1 : 0.7 }}
                                                />
                                                <label
                                                    className={`badge rounded-pill px-2 py-1 fw-semibold ${isLaboral ? 'bg-success' : 'bg-secondary'}`}
                                                    htmlFor={`switchDia_${item.iDiaSemana}`}
                                                    style={{ fontSize: '0.72rem', cursor: isEditing ? 'pointer' : 'default' }}
                                                >
                                                    {isLaboral ? 'Laboral' : 'Inactivo'}
                                                </label>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default BusinessHoursSettingsSection;
